import { faChartPie } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react';
import { Card, Row, Col } from 'react-bootstrap';

interface SalesTargetSummaryData {
    totalTarget: number;
    totalAchieved: number;
    overAchievers: number;
    underPerformers: number;
}

interface ModernSalesTargetSummaryChartProps {
    data: SalesTargetSummaryData;
    height?: number;
}

interface PieSegment {
    value: number;
    name: string;
    color: string;
    lightColor: string;
    percentage: number;
}

const ModernSalesTargetSummaryChart: React.FC<ModernSalesTargetSummaryChartProps> = ({
    data,
    height = 320,
}) => {
    const [animatedValues, setAnimatedValues] = useState<number[]>([0, 0]);

    // Função para formatar valores monetários
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };
    const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    const progressPercentage = (data.totalAchieved / data.totalTarget) * 100;
    const total = data.overAchievers + data.underPerformers;

    const segments: PieSegment[] = [
        {
            value: data.overAchievers,
            name: 'Meta Batida',
            color: '#10b981',
            lightColor: '#d1fae5',
            percentage: total > 0 ? (data.overAchievers / total) * 100 : 0
        },
        {
            value: data.underPerformers,
            name: 'Abaixo da Meta',
            color: '#f59e0b',
            lightColor: '#fef3c7',
            percentage: total > 0 ? (data.underPerformers / total) * 100 : 0
        }
    ];

    // Animação dos valores
    useEffect(() => {
        const timer = setTimeout(() => {
            setAnimatedValues([data.overAchievers, data.underPerformers]);
        }, 200);
        return () => clearTimeout(timer);
    }, [data]);

    // Função para gerar o SVG do gráfico de pizza
    const generatePieChart = () => {
        const centerX = 200;
        const centerY = 180;
        const radius = 140;
        const innerRadius = 90;
        
        let currentAngle = -90; // Começar do topo
        const paths: JSX.Element[] = [];

        segments.forEach((segment, index) => {
            if (segment.value === 0) return;

            const angle = (segment.value / total) * 360;
            const startAngleRad = (currentAngle * Math.PI) / 180;
            const endAngleRad = ((currentAngle + angle) * Math.PI) / 180;

            // Coordenadas do arco externo
            const x1 = centerX + radius * Math.cos(startAngleRad);
            const y1 = centerY + radius * Math.sin(startAngleRad);
            const x2 = centerX + radius * Math.cos(endAngleRad);
            const y2 = centerY + radius * Math.sin(endAngleRad);

            // Coordenadas do arco interno
            const x3 = centerX + innerRadius * Math.cos(endAngleRad);
            const y3 = centerY + innerRadius * Math.sin(endAngleRad);
            const x4 = centerX + innerRadius * Math.cos(startAngleRad);
            const y4 = centerY + innerRadius * Math.sin(startAngleRad);

            const largeArcFlag = angle > 180 ? 1 : 0;

            const pathData = [
                `M ${x1} ${y1}`,
                `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                `L ${x3} ${y3}`,
                `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
                'Z'
            ].join(' ');

            const isHovered = hoveredSegment === index;
            const scale = isHovered ? 1.05 : 1;

            paths.push(
                <g key={index} style={{ transformOrigin: `${centerX}px ${centerY}px` }}>
                    {/* Sombra */}
                    <path
                        d={pathData}
                        fill="rgba(0,0,0,0.1)"
                        transform={`translate(3, 4) scale(${scale})`}
                    />
                    {/* Segmento principal */}
                    <path
                        d={pathData}
                        fill={`url(#gradient-${index})`}
                        stroke="var(--phoenix-body-bg)"
                        strokeWidth="4"
                        transform={`scale(${scale})`}
                        style={{
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            filter: isHovered ? 'brightness(1.1)' : 'none'
                        }}
                        onMouseEnter={(e) => {
                            setHoveredSegment(index);
                            const rect = e.currentTarget.getBoundingClientRect();
                            setMousePosition({ x: e.clientX, y: e.clientY });
                        }}
                        onMouseMove={(e) => {
                            setMousePosition({ x: e.clientX, y: e.clientY });
                        }}
                        onMouseLeave={() => setHoveredSegment(null)}
                    />
                </g>
            );

            currentAngle += angle;
        });

        return paths;
    };

    // Função para renderizar o tooltip
    const renderTooltip = () => {
        if (hoveredSegment === null) return null;
        
        const segment = segments[hoveredSegment];
        const targetValue = hoveredSegment === 0 
            ? data.totalTarget * (segment.value / total)
            : data.totalTarget * (segment.value / total);
        const achievedValue = hoveredSegment === 0
            ? data.totalAchieved * (segment.value / total)
            : data.totalAchieved * (segment.value / total);
        
        // A porcentagem da fatia em relação ao total da pizza
        const fatiaPercentage = segment.percentage;

        return (
            <div
                style={{
                    position: 'fixed',
                    left: mousePosition.x - 150,
                    top: mousePosition.y - 40,
                    background: 'var(--phoenix-body-bg)',
                    color: 'var(--phoenix-body-emphasis-color)',
                    padding: '16px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontFamily: 'Inter, -apple-system, sans-serif',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                    border: `2px solid ${segment.color}`,
                    zIndex: 1000,
                    pointerEvents: 'none',
                    width: '140px'
                }}
            >
                {/* Porcentagem da Meta - PRINCIPAL */}
                <div style={{
                    textAlign: 'center',
                    marginBottom: '12px'
                }}>
                    <div style={{
                        fontSize: '32px',
                        fontWeight: '900',
                        color: segment.color,
                        lineHeight: '1',
                        marginBottom: '4px'
                    }}>
                        {Math.round(fatiaPercentage)}%
                    </div>
                    <div style={{
                        fontSize: '10px',
                        color: 'var(--phoenix-body-tertiary-color)',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        do Time
                    </div>
                </div>
                
                {/* Informações resumidas */}
                <div style={{
                    textAlign: 'center',
                    fontSize: '11px',
                    color: 'var(--phoenix-body-tertiary-color)',
                    lineHeight: '1.4'
                }}>
                    <div style={{ marginBottom: '2px' }}>
                        {formatCurrency(achievedValue)} / {formatCurrency(targetValue)}
                    </div>
                    <div>
                        {segment.value} vendedores
                    </div>
                </div>
                
                {/* Seta lateral */}
                <div style={{
                    position: 'absolute',
                    right: '-8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '0',
                    height: '0',
                    borderTop: '6px solid transparent',
                    borderBottom: '6px solid transparent',
                    borderLeft: `8px solid ${segment.color}`
                }} />
            </div>
        );
    };

    return (
        <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="border-0 py-3" style={{ 
                borderRadius: '16px 16px 0 0',
                borderBottom: '1px solid var(--phoenix-tertiary-bg)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                   <FontAwesomeIcon icon={faChartPie} className="text-info" style={{ fontSize: '18px' }} />
                   
                    <div>
                        <h6 className="mb-1 text-body-emphasis fw-bold" style={{ 
                            fontSize: '16px',
                            fontFamily: 'Inter, -apple-system, sans-serif'
                        }}>
                            Resumo de Performance
                        </h6>
                        <p className="mb-0 text-body-tertiary" style={{ 
                            fontSize: '12px',
                            fontWeight: '500'
                        }}>
                            Distribuição por atingimento de meta
                        </p>
                    </div>
                </div>
            </Card.Header>
            
            <Card.Body className="p-5">
                {/* Verificar se há dados para exibir */}
                {total === 0 ? (
                    <div 
                        className="text-center d-flex flex-column align-items-center justify-content-center"
                        style={{ height: `${height - 100}px` }}
                    >
                        <FontAwesomeIcon
                            icon={faChartPie}
                            size="3x"
                            className="text-muted mb-3"
                        />
                        <h5 className="text-muted">
                            Nenhuma meta encontrada
                        </h5>
                        <p className="text-muted">
                            Configure metas para a equipe para visualizar o
                            resumo de performance.
                        </p>
                    </div>
                ) : (
                <>
                <div className="d-flex justify-content-center" style={{ position: 'relative' }}>
                    <svg width="400" height="360" viewBox="0 0 400 360">
                        {/* Definições de gradientes */}
                        <defs>
                            {segments.map((segment, index) => (
                                <linearGradient key={index} id={`gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor={segment.color} />
                                    <stop offset="100%" stopColor={`${segment.color}dd`} />
                                </linearGradient>
                            ))}
                        </defs>
                        
                        {/* Círculo de fundo */}
                        <circle
                            cx="200"
                            cy="180"
                            r="140"
                            fill="none"
                            stroke="var(--phoenix-tertiary-bg)"
                            strokeWidth="3"
                        />
                        
                        {/* Segmentos do gráfico */}
                        {generatePieChart()}
                        
                        {/* Círculo central com porcentagem */}
                        <circle
                            cx="200"
                            cy="180"
                            r="85"
                            fill="var(--phoenix-body-bg)"
                            stroke="var(--phoenix-tertiary-bg)"
                            strokeWidth="3"
                        />
                        
                        {/* Texto central */}
                        <text
                            x="200"
                            y="170"
                            textAnchor="middle"
                            style={{
                                fontSize: '36px',
                                fontWeight: '800',
                                fill: progressPercentage >= 100 ? '#10b981' : progressPercentage >= 80 ? '#f59e0b' : '#ef4444',
                                fontFamily: 'Inter, -apple-system, sans-serif'
                            }}
                        >
                            {Math.round(progressPercentage)}%
                        </text>
                        <text
                            x="200"
                            y="195"
                            textAnchor="middle"
                            style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                fill: 'var(--phoenix-body-color)',
                                fontFamily: 'Inter, -apple-system, sans-serif'
                            }}
                        >
                            Meta Geral
                        </text>
                        <text
                            x="200"
                            y="215"
                            textAnchor="middle"
                            style={{
                                fontSize: '14px',
                                fontWeight: '500',
                                fill: 'var(--phoenix-body-color)',
                                fontFamily: 'Inter, -apple-system, sans-serif'
                            }}
                        >
                            {total} vendedores
                        </text>
                    </svg>
                </div>
                
                {/* Totais na parte inferior */}
                <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--phoenix-tertiary-bg)' }}>
                    <Row className="text-center gap-2">
                        <Col>
                            <div className="bg-body-secondary" style={{
                                borderRadius: '12px',
                                padding: '16px'
                            }}>
                                <div className="text-body-emphasis" style={{
                                    fontSize: '20px',
                                    fontWeight: '800',
                                    marginBottom: '4px',
                                    fontFamily: 'Inter, -apple-system, sans-serif'
                                }}>
                                    {formatCurrency(data.totalAchieved)}
                                </div>
                                <div className="text-body-tertiary" style={{
                                    fontSize: '12px',
                                    fontWeight: '600'
                                }}>
                                    Total Vendido
                                </div>
                            </div>
                        </Col>
                        <Col>
                            <div className="bg-body-secondary" style={{
                                borderRadius: '12px',
                                padding: '16px'
                            }}>
                                <div className="text-body-emphasis" style={{
                                    fontSize: '20px',
                                    fontWeight: '800',
                                    marginBottom: '4px',
                                    fontFamily: 'Inter, -apple-system, sans-serif'
                                }}>
                                    {formatCurrency(data.totalTarget)}
                                </div>
                                <div className="text-body-tertiary" style={{
                                    fontSize: '12px',
                                    fontWeight: '600'
                                }}>
                                    Meta Total
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
                </>
                )}
            </Card.Body>
            
            {/* Tooltip */}
            {renderTooltip()}
        </Card>
    );
};

export default ModernSalesTargetSummaryChart;
